import { useEffect, useState } from 'react'
import { useGetGallery, useGetWishlist } from './api'
import { useGetCatalog } from '@/shared/edit-post-with-gallery/api/single-post-api'
import ImageGrid from './components/ImageGrid'
import ContextProvider from './context/ContextProvider'
import { AnimatePresence, motion } from 'motion/react'
import UploadImageInput from './components/UploadImageInput'
import { Button } from '@/shared/ui/button.tsx'
import { Heart, ImagePlus, Video } from 'lucide-react'
// import CatalogCarousel from './components/CatalogCarousel'
import { Card } from '@/shared/ui/card.tsx'
import { cn } from '@/lib/utils'
import { useGetVideoGallery } from './api/index'
import VideoGrid from './components/VideoGrid'
import { toast } from 'sonner'
import { useAtomValue } from 'jotai'
import { themeAtom } from '../app-layout/atoms/theme-atoms'
import { useSearchParams } from 'react-router-dom'

export type ViewType = "upload" | "wishlist" | "videos" | "gallery"

const NewGalleryView = () => {

  const theme = useAtomValue(themeAtom)

  const [params, _] = useSearchParams();

  const currentView = params.get("view")

  const [view, setView] = useState<ViewType>(currentView !== null ? currentView as ViewType : "gallery")

  const { gallery, fetchNextPage, hasNextPage } = useGetGallery(true)

  const { wishlist, fetchNextPage: fetchNextWishlist, hasNextPage: hasNextWishlist } = useGetWishlist(true)

  const { catalogData } = useGetCatalog(true)

  const [catalogItem, setCatalogItem] = useState("")

  useEffect(() => {
    if (catalogData && catalogData.catalogue.length > 0) {
      setCatalogItem(catalogData.catalogue[0].unique_identifier)
    }
  }, [catalogData])


  const { gallery: videos, fetchNextPage: fetchNextVideosPage, hasNextPage: hasNextVideo } = useGetVideoGallery(view === "videos")

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  const direction = view === "upload" ? 1 : -1;

  return (
    <ContextProvider>
      <div className="mt-[5vh] w-full h-[95vh] p-5 md:p-10 2xl:p-14 overflow-auto flex flex-col gap-4 xl:gap-8">
        <h1 className="text-2xl animate-in slide-in-from-left-32 fade-in ease-in duration-1000 md:text-5xl font-header font-medium">Gallery</h1>
        <div className='size-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>

          <div className='flex flex-col xl:flex-row items-start xl:items-center gap-2 border-y w-full'>
            <div className="w-full flex-1 flex items-center gap-2 py-3 flex-wrap">
              {catalogData && catalogData.catalogue.map(item => (
                <Card
                  key={item.name}
                  onClick={() => {
                    setView("gallery")
                    setCatalogItem(item.unique_identifier)
                  }}
                  className={cn("px-4 py-2 text-xs font-poppins cursor-pointer rounded-full tracking-wide",
                    (catalogItem === item.unique_identifier && view === "gallery") ? "bg-purple-300" : ""
                  )}>{item.name}</Card>
              ))}
            </div>
            <div className='flex items-center gap-2 pb-2'>
              <Button
                size="icon"
                variant="outline"
                onClick={() => {
                  if (theme.isVideoClient) {
                    setView("videos")
                  } else {
                    toast.message("Coming soon🎃...")
                  }
                }}
                className='flex items-center gap-2 rounded-full border-purple-400'>
                <Video className='text-purple-500' />
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => {
                  setView("wishlist")
                }}
                className='flex items-center gap-2 rounded-full border-purple-400'>
                <Heart className='text-purple-500' strokeWidth='3px' />
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => {
                  setView("upload")
                }}
                className='flex items-center gap-2 rounded-full border-purple-400'>
                <ImagePlus className='text-purple-500' />
              </Button>
            </div>
          </div>
          <AnimatePresence initial={false} custom={direction} mode="wait">

            {(view === "gallery") && <motion.div key="first"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.5,
                ease: "easeInOut"
              }}
              className="flex-1 overflow-y-auto p-4">
              {gallery &&
                <ImageGrid
                  galleryImages={gallery.find(item => item.catalogue === catalogItem)?.images}
                  fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                />
              }
            </motion.div>}

            {(view === "upload") && <motion.div key="second"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.5,
                ease: "easeInOut"
              }} className="flex-1 overflow-y-auto p-4">
              {catalogData &&
                <UploadImageInput
                  setCatalog={setCatalogItem}
                  changeView={(vw: ViewType) => setView(vw)}
                  catalogData={catalogData.catalogue}
                  prompt="" />
              }
            </motion.div>
            }
            {(view === "videos") && (
              <motion.div key="fourth"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.5,
                  ease: "easeInOut"
                }}
                className="flex-1 overflow-y-auto p-4">
                {videos &&

                  <VideoGrid
                    galleryImages={videos}
                    fetchNextPage={fetchNextVideosPage}
                    hasNextPage={hasNextVideo}
                  />
                }
              </motion.div>
            )}
            {(view === "wishlist") && (
              <motion.div key="third"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.5,
                  ease: "easeInOut"
                }}
                className="flex-1 overflow-y-auto p-4">
                {wishlist &&
                  <ImageGrid
                    galleryImages={wishlist}
                    fetchNextPage={fetchNextWishlist}
                    hasNextPage={hasNextWishlist}
                  />
                }
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </ContextProvider>
  )
}

export default NewGalleryView
